import { useState, useEffect, useRef, useContext } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Typography,
  Box,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import LogoutIcon from "@mui/icons-material/Logout";
import HomeIcon from "@mui/icons-material/Home";
import PersonIcon from "@mui/icons-material/Person";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { getInitials } from "../../utils/getName";
import { END_POINTS } from "../Constants";
import { useNavigate } from "react-router-dom";
import { CandidateContext } from "../Context";
import axiosInstance from "../../Api/Instance";
import { useDispatch } from "react-redux";
import { logout } from "../../redux/authSlice";

const { CANDIDATE_LIST, CANDIDATE } = END_POINTS;

const Header = ({ userInfo, setUserInfo, setIsLoggedIn, userId }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { notificationSeen, setIsNotificationSeen } = useContext(CandidateContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const dispatch = useDispatch();

  const fetchNotifications = async () => {
    try {
      const response = await axiosInstance.get(`/get-shared-candidates/${userId}`);
      const unreadCount = response.data.data.filter((notif) => notif.isRead === false).length;
      setNotificationCount(unreadCount);
      setNotifications(response.data.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 120000);
      return () => clearInterval(interval);
    }
  }, [userId, notificationSeen]);

  const handleHome = () => {
    navigate(CANDIDATE_LIST);
  };

  // const handleLogout = async () => {
  //   try {
  //     await axiosInstance.post(`/logout`, {}, { withCredentials: true });
  //     dispatch(logout());
  //     localStorage.removeItem("authState");
  //     setAnchorEl(null);
  //     setUserInfo(null);
  //     setIsLoggedIn(false);
  //     window.location.href = "/login";
  //   } catch (error) {
  //     console.error("Logout failed:", error);
  //   }
  // };

  const handleLogout = async () => {
  try {

    await axiosInstance.post("/logout", {}, { withCredentials: true });

  } catch (error) {
    console.warn("Backend logout failed, clearing client session anyway");
  } finally {
    dispatch(logout());
    navigate("/login");
  }
};
  const handleNotificationClick = (notification) => {
    navigate(`${CANDIDATE}/${notification.candidateId._id}`, {
      state: { candidate: notification.candidateId },
    });
    setIsNotificationSeen(!notificationSeen);
    setNotificationAnchor(null);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationOpen = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar className="justify-between px-4 md:px-6">
        {/* Left Section */}
        <Box className="flex items-center gap-4">
          <IconButton
            onClick={handleHome}
            sx={{
              color: '#fff',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            <HomeIcon />
          </IconButton>
          
          {!isMobile && (
            <Box>
              <Typography variant="h6" className="text-white font-bold">
                Candidate Management System
              </Typography>
              <Typography variant="caption" className="text-white/80">
                Welcome back, {userInfo?.name}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Right Section */}
        <Box className="flex items-center gap-2 md:gap-3">
          {/* Notifications */}
          <IconButton
            onClick={handleNotificationOpen}
            sx={{
              color: '#fff',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <Badge 
              badgeContent={notificationCount} 
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: '#ef4444',
                  animation: notificationCount > 0 ? 'pulse 2s ease-in-out infinite' : 'none',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                  },
                },
              }}
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* User Avatar */}
          <Box 
            onClick={handleMenuOpen}
            className="flex items-center gap-2 cursor-pointer hover:bg-white/10 rounded-full px-3 py-1 transition-all"
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                backgroundColor: '#60a5fa',
                fontSize: 16,
                fontWeight: 600,
                border: '2px solid rgba(255, 255, 255, 0.3)',
              }}
            >
              {getInitials(userInfo?.name)}
            </Avatar>
            {!isMobile && (
              <Typography variant="body2" className="text-white font-semibold">
                {userInfo?.name}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Notifications Popover */}
        <Popover
          open={Boolean(notificationAnchor)}
          anchorEl={notificationAnchor}
          onClose={handleNotificationClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              mt: 1,
              width: 400,
              maxWidth: '90vw',
              maxHeight: 500,
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            },
          }}
        >
          <Box className="p-4 border-b border-gray-200">
            <Typography variant="h6" className="font-bold text-gray-800">
              Notifications
            </Typography>
            <Typography variant="caption" className="text-gray-500">
              You have {notificationCount} unread notifications
            </Typography>
          </Box>

          <List sx={{ p: 0, maxHeight: 400, overflow: 'auto' }}>
            {notifications.length > 0 ? (
              notifications.map((notification) => (
                <ListItem
                  key={notification._id}
                  button
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    borderBottom: '1px solid #f3f4f6',
                    py: 2,
                    px: 3,
                    '&:hover': {
                      backgroundColor: '#f9fafb',
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        backgroundColor: notification.isRead ? '#e5e7eb' : '#3b82f6',
                        width: 48,
                        height: 48,
                      }}
                    >
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box className="flex items-center justify-between gap-2">
                        <Typography variant="body2" className="font-semibold text-gray-800">
                          Review Candidate: {notification.candidateId.name}
                        </Typography>
                        {!notification.isRead && (
                          <FiberManualRecordIcon 
                            sx={{ fontSize: 10, color: '#3b82f6' }} 
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" className="text-gray-600">
                        Shared by {notification.sharedBy.name}
                      </Typography>
                    }
                  />
                </ListItem>
              ))
            ) : (
              <Box className="flex flex-col items-center justify-center py-8">
                <NotificationsIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 2 }} />
                <Typography variant="body2" className="text-gray-500">
                  No new notifications
                </Typography>
              </Box>
            )}
          </List>
        </Popover>

        {/* User Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 200,
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <Box className="px-4 py-3 border-b border-gray-200">
            <Typography variant="body1" className="font-semibold text-gray-800">
              {userInfo?.name}
            </Typography>
            <Typography variant="caption" className="text-gray-500">
              {userInfo?.email}
            </Typography>
          </Box>

          {/* <MenuItem 
            onClick={handleHome}
            sx={{ py: 1.5, px: 3 }}
          >
            <HomeIcon sx={{ mr: 2, color: '#6b7280' }} />
            <Typography variant="body2">Home</Typography>
          </MenuItem> */}

          <Divider />

          <MenuItem 
            onClick={handleLogout}
            sx={{ 
              py: 1.5, 
              px: 3,
              color: '#ef4444',
              '&:hover': {
                backgroundColor: '#fee2e2',
              },
            }}
          >
            <LogoutIcon sx={{ mr: 2 }} />
            <Typography variant="body2" className="font-semibold">
              Logout
            </Typography>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
